import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { type Href, Redirect, Stack, usePathname, useSegments } from 'expo-router';
import Head from 'expo-router/head';
import { StatusBar } from 'expo-status-bar';
import * as Clarity from '@microsoft/react-native-clarity';
import 'react-native-reanimated';

import { getRoleHomePath } from '@/lib/auth-routing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { AuthProvider, useAuth } from '@/providers/auth-provider';

// Initialize Microsoft Clarity
Clarity.initialize('wokk4o6yry', {
  logLevel: Clarity.LogLevel.None,
});

export const unstable_settings = {
  anchor: '(tabs)',
};

export default function RootLayout() {
  const colorScheme = useColorScheme();

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <Head>
        <title>MCQGen</title>
        <meta
          name="description"
          content="MCQGen helps schools manage classes, teachers, students, assignments, and question sets from one admin workspace."
        />
        <meta name="theme-color" content="#163828" />
      </Head>
      <AuthProvider>
        <AuthGate />
        <Stack>
          <Stack.Screen name="index" options={{ headerShown: false, animation: 'fade' }} />
          <Stack.Screen name="(auth)" options={{ headerShown: false }} />
          <Stack.Screen name="admin" options={{ headerShown: false }} />
          <Stack.Screen name="teacher" options={{ headerShown: false }} />
          <Stack.Screen name="student" options={{ headerShown: false }} />
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
          <Stack.Screen name="modal" options={{ presentation: 'modal', title: 'Modal' }} />
        </Stack>
      </AuthProvider>
      <StatusBar style="auto" />
    </ThemeProvider>
  );
}

function AuthGate() {
  const { loading, profile, session } = useAuth();
  const segments = useSegments();
  const pathname = usePathname();

  if (loading) return null;

  const currentGroup = segments[0];
  const inAuth = currentGroup === '(auth)';
  const inTabs = segments[0] === '(tabs)';
  const inProtectedRoleGroup =
    currentGroup === 'admin' || currentGroup === 'teacher' || currentGroup === 'student';

  if (!session && (inTabs || inProtectedRoleGroup)) {
    const nextPath = pathname && pathname !== '/' ? pathname : '/admin';
    const signInHref = `/sign-in?next=${encodeURIComponent(nextPath)}` as Href;

    return <Redirect href={signInHref} />;
  }

  if (session && profile) {
    const targetGroup = getRoleHomePath(profile.role).slice(1);

    if (inAuth || currentGroup === '(tabs)') {
      return <Redirect href={getRoleHomePath(profile.role) as Href} />;
    }

    if (inProtectedRoleGroup && currentGroup !== targetGroup) {
      return <Redirect href={getRoleHomePath(profile.role) as Href} />;
    }
  }

  return null;
}
